import * as React from 'react';
import {
  IOverflowSetItemProps,
  IButton,
  OverflowSet,
  IconButton,
  ContextualMenu,
  IContextualMenuProps,
  styled,
  ITheme,
  IStyleFunctionOrObject,
  concatStyleSetsWithProps,
  IOverflowSetStyles,
  IButtonStyles,
  TooltipHost
} from '@fluentui/react';
import { CommandProps, CommandsUpdate } from '@fluidx/office-fluid-types';
import { useFFXHostUxIcon } from '@fluidx/icons';
import { generateTooltipId } from '../../utilities/generateTooltipId';
import { lightDismissMenuOnWindowBlur } from '../../utilities/lightDismissMenuOnWindowBlur';
import { getActionMenuPreviewItem } from './ActionMenuPreviewButton';
import { ComponentUXContext } from './ComponentUXContextProvider';

export interface ActionMenuProps {
  /** The items which will populate the menu */
  menuItems?: CommandProps[];

  /**
   * Whether the Preview item should be displayed.
   * If not provided, we will show the preview callout.
   * */
  hidePreviewItem?: boolean;

  /**
   * Custom styling for action menu controls
   */
  styles?: IStyleFunctionOrObject<ActionMenuStyleProps, ActionMenuStyles>;

  theme?: ITheme;
}

export interface ActionMenuStyles {
  subComponentStyles?: {
    /** Styling for the root element */
    overflowSet?: IOverflowSetStyles;

    /** Styling for the overflow button */
    overflowButton?: IButtonStyles;
  };
}

export const actionMenuTestId = 'actionMenuTestId';

interface ActionMenuStyleProps extends Pick<ActionMenuProps, 'theme'> {}

function getDefaultStyle(props: ActionMenuStyleProps): ActionMenuStyles {
  const { theme } = props;

  return {
    subComponentStyles: {
      overflowSet: {
        overflowButton: {
          // How the overflow set displays the button
          flexDirection: 'column',
          justifyContent: 'center'
        }
      },
      overflowButton: {
        root: {
          // How the button itself is styled
          fontSize: '14px',
          color: theme ? theme.palette.black : 'black'
        }
      }
    }
  };
}

export const ActionMenu = React.memo(
  styled(function ActionMenu(props: ActionMenuProps) {
    const { menuItems, hidePreviewItem, styles } = props;
    const { component, stringTable, tokenProvider, localization } = React.useContext(ComponentUXContext)!;

    const [componentCommands, setComponentCommands] = React.useState<CommandProps[]>();
    const [menuItemsList, setMenuItemsList] = React.useState<CommandProps[] | undefined>(menuItems);

    const menuButtonRef = React.createRef<IButton>();
    const onDismissMenu = React.useCallback((): void => {
      menuButtonRef.current?.dismissMenu();
    }, [menuButtonRef]);

    const resolvedStyles = concatStyleSetsWithProps(props, styles);

    React.useEffect(() => {
      updateMenuItems();
    }, [menuItems]);

    React.useEffect(() => {
      if (component?.ComponentProduceCommands) {
        // tslint:disable-next-line: no-floating-promises
        component.ComponentProduceCommands.getCommands().then((commands) => {
          commands && setComponentCommands(commands);
        });
      }
    }, [component]);

    React.useEffect(() => {
      component?.ComponentProduceCommands?.on('commandsUpdated', (commandsUpdates) => {
        onCommandsUpdated(commandsUpdates);
      });
      updateMenuItems();
    }, [componentCommands]);

    const onCommandsUpdated = (commandsUpdates: CommandsUpdate) => {
      if (!componentCommands) {
        return;
      }
      let updatedCommands: CommandProps[] = componentCommands;
      for (let index in commandsUpdates) {
        const commandIndex = componentCommands.findIndex((value) => value.id === index);
        if (commandIndex !== undefined && commandIndex > -1) {
          const newValue = { ...componentCommands[commandIndex], ...commandsUpdates[index] };
          updatedCommands[commandIndex] = newValue;
        }
      }
      setComponentCommands(updatedCommands);
      updateMenuItems();
    };

    const updateMenuItems = () => {
      if (!menuItems && !componentCommands) {
        setMenuItemsList(undefined);
        return;
      }
      const commandsFromHost = menuItems || [];
      const commandsFromComponent = componentCommands || [];
      const items = [...commandsFromHost, ...commandsFromComponent];
      setMenuItemsList(items);
    };

    const overflowMenuItems = [];

    if (!hidePreviewItem) {
      overflowMenuItems.push(
        getActionMenuPreviewItem({
          localization,
          previewButtonLabelText: stringTable.previewButtonLabel,
          onPrimaryButtonClick: onDismissMenu,
          identityType: tokenProvider?.identityType || 'Enterprise' // Default to enterprise when identity type is not known
        })
      );
    }

    if (menuItemsList) {
      // Map CommandProps to OverFlowSetItem to populate overflow menu
      for (const menuItem of menuItemsList) {
        // Remove properties that are not used by IOverflowSetItemProps
        const { id, icon, displayName, execute, ...otherProperties } = menuItem;
        const item: IOverflowSetItemProps = {
          ...otherProperties,
          key: menuItem.id,
          iconProps: { iconName: menuItem.icon },
          name: menuItem.displayName,
          onClick: menuItem.execute
        };
        overflowMenuItems.push(item);
      }
    }

    if (overflowMenuItems.length === 0) {
      return null;
    }

    const renderMenu = (props: IContextualMenuProps): JSX.Element => {
      lightDismissMenuOnWindowBlur(onDismissMenu);

      const onRestoreFocus = (options: {
        originalElement?: HTMLElement | Window;
        containsFocus: boolean;
        documentContainsFocus: boolean;
      }) => {
        if (options && options.containsFocus && options.documentContainsFocus && options.originalElement) {
          options.originalElement.focus();
        }
      };

      return <ContextualMenu {...props} onRestoreFocus={onRestoreFocus} />;
    };

    /**
     * @param overflowItems The set of items which should show in the '...' menu
     */
    const onRenderOverflowButton = (overflowItems: any[] | undefined) => {
      if (!overflowItems) {
        return null;
      }

      const tooltipId = generateTooltipId();
      const entryPointTitle = stringTable.actionMenuOverflowButtonTitle;

      return (
        <TooltipHost content={entryPointTitle} id={tooltipId}>
          <IconButton
            aria-label={entryPointTitle}
            aria-describedby={tooltipId}
            role="menuitem"
            componentRef={menuButtonRef}
            styles={resolvedStyles?.subComponentStyles?.overflowButton}
            menuIconProps={moreIconProps}
            menuProps={{
              items: overflowItems
            }}
            menuAs={renderMenu}
          />
        </TooltipHost>
      );
    };

    return (
      <OverflowSet
        overflowItems={overflowMenuItems}
        onRenderItem={onRenderItem}
        onRenderOverflowButton={onRenderOverflowButton}
        styles={resolvedStyles?.subComponentStyles?.overflowSet}
        data-testid={actionMenuTestId}
        role="menubar"
      />
    );
  }, getDefaultStyle)
);

/**
 * Unused since we're currently not displaying any items other than the
 * overflow menu
 */
function onRenderItem(_item: IOverflowSetItemProps) {}

const moreIconProps = { iconName: useFFXHostUxIcon('FFXMore') };

// cSpell:ignore testid
